/**
 * Component hiển thị widget chat với nội dung (khóa học, bài học, sơ đồ)
 * Cho phép người dùng hỏi đáp với chuyên gia phù hợp về nội dung
 * Đây là component chính thức đang hoạt động
 */
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  MessageCircle,
  Coins,
  X,
  AlertTriangle,
  MessageSquare,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import ChatWithAI from "./ChatWithAI";
import { Expert, DEFAULT_EXPERT, ExpertField } from "@/models/Expert";
import { lessonExpertService } from "@/services/lessonExpertService";
import { courseExpertService } from "@/services/courseExpertService";
import { expertService } from "@/services/expertService";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { logger } from "@/utils/logger";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { getAIChatPoints } from "@/config/pointsConfig";
import usePoints from "@/hooks/usePoints";
import { courseService } from "@/services/courseService";

// Khai báo kiểu cho window.courseData
declare global {
  interface Window {
    courseData?: {
      id: string;
      name: string;
      description?: string;
      nodes?: Array<CourseNode>;
    };
    courseNodes?: Array<CourseNode>;
  }
}

// Định nghĩa interface cho resource
interface Resource {
  url: string;
  type: string;
  title: string;
  description: string;
}

// Định nghĩa interface cho node
interface CourseNode {
  id: string;
  name: string;
  type: string;
  content: string;
  children?: string[];
  resources?: Resource[];
  description?: string;
}

// Định nghĩa interface cho contextDataObj
interface ContextData {
  expertId: string;
  expertName: string;
  expertTitle: string;
  expertAvatar: string;
  expertField: string;
  expertDescription: string;
  greeting: string;
  contextType: ContentType;
  contextId: string;
  contextName: string;
  contextDescription: string;
  contextData: string;
  lessonData: any;
  diagramData: any;
  suggestedQuestions: string[];
  pointsPerChat: number;
  system_instruction: string;
  course_contents?: {
    id: string;
    name: string;
    description: string;
    nodes: CourseNode[];
    currentNodeIndex: number;
  };
  courseName: string;
}

export type ContentType =
  | "course"
  | "lesson"
  | "diagram"
  | "meditation"
  | "sutra"
  | "practice";

interface ContentChatWidgetProps {
  // Thông tin chung
  contentType: ContentType;
  contentName: string;
  contentId: string;
  contentDescription?: string;
  contentData?: string;
  categoryId?: string;
  field?: ExpertField;

  // Thông tin bổ sung cho lesson
  courseId?: string;

  // Thông tin bổ sung cho diagram
  diagramData?: any;

  // Tùy chọn hiển thị
  className?: string;

  // Tùy chọn đóng
  onClose?: () => void;

  // Gợi ý câu hỏi
  suggestedQuestions?: string[];

  // Tùy chọn full-screen
  isDialog?: boolean;

  // Tùy chọn ẩn header
  hideHeader?: boolean;

  // Tùy chọn ẩn nút bắt đầu chat
  hideStartChatButton?: boolean;

  // Tùy chọn ẩn điểm
  hidePoints?: boolean;

  // Tùy chọn ẩn nút đóng
  hideCloseButton?: boolean;
  
  // Tùy chọn bỏ qua lưu lịch sử chat
  skipSaveHistory?: boolean;
}

/**
 * Component hiển thị widget chat với nội dung (khóa học, bài học, sơ đồ)
 * Cho phép người dùng hỏi đáp với chuyên gia phù hợp về nội dung
 */
const ContentChatWidget: React.FC<ContentChatWidgetProps> = ({
  contentType,
  contentName,
  contentId,
  contentDescription = "",
  contentData = "",
  categoryId = "general",
  field = ExpertField.GENERAL,
  courseId = "",
  diagramData = null,
  className = "",
  onClose,
  suggestedQuestions = [],
  isDialog = false,
  hideHeader = false,
  hideStartChatButton = false,
  hidePoints = false,
  hideCloseButton = false,
  skipSaveHistory = false,
}) => {
  const { t } = useTranslation();
  const [showChat, setShowChat] = useState(true);
  const [expert, setExpert] = useState<Expert>(DEFAULT_EXPERT); // Luôn có giá trị mặc định
  const [loading, setLoading] = useState(true);
  const [courseNodes, setCourseNodes] = useState<CourseNode[]>([]);
  const [isFetchingCourse, setIsFetchingCourse] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Kiểm tra nếu đang ở mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);
  
  // Sử dụng hook usePoints
  const { 
    points: aiCredits, 
    loading: pointsLoading, 
    error: pointsError,
    fetchPoints 
  } = usePoints();
  
  // Lấy số điểm cần thiết dựa trên loại nội dung
  const requiredPoints = contentType ? getAIChatPoints(contentType) : getAIChatPoints("course");
  
  // Kiểm tra xem có đủ điểm để chat không
  const hasEnoughPoints = aiCredits !== null && aiCredits >= requiredPoints;

  // Lấy dữ liệu khóa học nếu cần
  useEffect(() => {
    const fetchCourseData = async () => {
      // Bỏ qua nếu là diagram
      if (contentType === "diagram") {
        return;
      }
      
      // Chỉ lấy dữ liệu nếu là khóa học hoặc bài học và có courseId
      if ((contentType === "course" || contentType === "lesson") && 
          (courseId || contentId)) {
        try {
          setIsFetchingCourse(true);
          const id = contentType === "course" ? contentId : courseId;
          logger.info(`Fetching course data for ${id}`);
          
          if (window.courseData?.id === id && window.courseData?.nodes?.length) {
            // Sử dụng dữ liệu đã có nếu khớp với khóa học hiện tại
            logger.info(`Using cached course data for ${id}`);
            setCourseNodes(window.courseData.nodes);
            setIsFetchingCourse(false);
            return;
          }
          
          const course = await courseService.getCourseById(id);
          
          if (course && course.nodes && course.nodes.length > 0) {
            logger.info(`Found course with ${course.nodes.length} nodes`);
            setCourseNodes(course.nodes);
            
            // Lưu vào window để các component khác có thể sử dụng
            window.courseData = course;
            window.courseNodes = course.nodes;
          } else {
            // Nếu không phải khóa học, không cần báo lỗi
            if (contentType === "lesson") {
              logger.info(`No nodes found for course ${id}, but it's a lesson, so it's fine`);
            } else {
              logger.warn(`No nodes found for course ${id}`);
            }
          }
        } catch (error) {
          logger.error(`Error fetching course data:`, error);
        } finally {
          setIsFetchingCourse(false);
        }
      }
    };
    
    fetchCourseData();
  }, [contentType, contentId, courseId]);

  // Lấy chuyên gia phù hợp cho nội dung
  useEffect(() => {
    const fetchExpert = async () => {
      try {
        setLoading(true);
        logger.info(`Fetching expert for ${contentType}:`, {
          contentId,
          categoryId,
          field,
        });

        let bestExpert: Expert | null = null;

        // Dựa vào loại nội dung để lấy chuyên gia phù hợp
        if (contentType === "lesson" && courseId) {
          // 1. Thử lấy chuyên gia được gán trực tiếp cho bài học
          bestExpert = await lessonExpertService.getExpertForLesson(
            courseId,
            contentId,
          );

          // 2. Nếu không có, thử lấy chuyên gia được gán cho khóa học
          if (!bestExpert) {
            logger.info("No lesson expert found, trying course expert");
            bestExpert = await courseExpertService.getExpertForCourse(courseId);
          }
        } else if (contentType === "course") {
          // 1. Thử lấy chuyên gia được gán cho khóa học
          bestExpert = await courseExpertService.getExpertForCourse(contentId);
        }

        // Nếu vẫn không có, lấy chuyên gia mặc định cho lĩnh vực
        if (!bestExpert) {
          logger.info(
            `No ${contentType} expert found, using default expert for field:`,
            field,
          );
          bestExpert = await expertService.getDefaultExpertForField(field);
        }

        logger.info("Expert fetched:", bestExpert?.name || "DEFAULT_EXPERT");

        // Luôn đảm bảo có một chuyên gia, sử dụng DEFAULT_EXPERT nếu không tìm thấy
        setExpert(bestExpert || DEFAULT_EXPERT);
      } catch (error) {
        logger.error(`Error fetching expert for ${contentType}:`, error);
        // Sử dụng chuyên gia mặc định nếu có lỗi
        setExpert(DEFAULT_EXPERT);
      } finally {
        setLoading(false);
      }
    };

    fetchExpert();
  }, [contentType, contentId, courseId, categoryId, field]);

  // Xử lý khi gửi tin nhắn thành công
  const handleMessageSent = async () => {
    try {
      // Cập nhật lại số điểm
      await fetchPoints();
    } catch (error) {
      console.error("Lỗi khi cập nhật điểm:", error);
    }
  };

  // Chuẩn bị dữ liệu context cho AI
  const prepareContextData = () => {
    return {
      expertId: expert?.id || "default",
      expertName: expert?.name || "Thiền Sư AI",
      expertTitle: expert?.title || "Thiền sư",
      expertAvatar: expert?.avatar || "",
      expertField: expert?.field || "BUDDHISM",
      expertDescription: expert?.description || "",
      greeting: expert?.greeting || "",
      contextType: contentType,
      contextId: contentId,
      contextName: contentName,
      contextDescription: contentDescription,
      contextData: contentData,
      courseName: contentType === "lesson" ? window.courseData?.name || "" : contentName,
      lessonData: contentType === "lesson" ? { content: contentData, name: contentName } : null,
      diagramData: contentType === "diagram" ? diagramData : null,
      suggestedQuestions: suggestedQuestions,
      pointsPerChat: requiredPoints,
      system_instruction: `Bạn là Thiền Sư AI đang trả lời về ${contentType === "lesson" ? `bài học "${contentName}"` : 
                          contentType === "course" ? `khóa học "${contentName}"` : 
                          contentType === "diagram" ? `sơ đồ "${contentName}"` : 
                          contentType === "meditation" ? `bài thiền "${contentName}"` : 
                          contentType === "sutra" ? `kinh "${contentName}"` : 
                          contentType === "practice" ? `bài thực hành "${contentName}"` : 
                          "nội dung Phật giáo"}.
                          
Hãy trả lời dựa trên nội dung: "${contentData?.substring(0, 500)}..."

Tuân thủ các nguyên tắc sau:
1. Trả lời ngắn gọn, súc tích, phù hợp với giao diện mobile
2. Độ dài trả lời không quá 200 từ cho mỗi câu hỏi
3. Chia thành các đoạn ngắn, mỗi đoạn 2-3 câu
4. Nếu cần liệt kê, hãy sử dụng dạng danh sách với số thứ tự rõ ràng
5. Sử dụng ngôn ngữ đơn giản, dễ hiểu cho người mới học Phật
6. Không lặp lại nội dung người dùng đã biết
7. Sử dụng ngôn ngữ thân thiện, gần gũi khi trò chuyện`
    };
  };

  return (
    <Card
      className={cn(className, "flex flex-col flex-1 overflow-hidden")}
      style={{ 
        height: isDialog ? '100%' : 'calc(85vh - 40px)',
        maxHeight: isDialog ? '100%' : '85vh',
        border: isDialog ? 'none' : undefined, 
        boxShadow: isDialog ? 'none' : undefined, 
        borderRadius: isDialog ? 0 : undefined 
      }}
    >
      {!hideHeader && (
        <CardHeader className="px-4 py-3 border-b shrink-0">
          {loading ? (
            <div className="flex items-center space-x-4">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-[250px]" />
                <Skeleton className="h-3 w-[150px]" />
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage
                    src={expert?.avatar}
                    alt={expert?.name || "Expert"}
                  />
                  <AvatarFallback>
                    {(expert?.name || "E").charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center">
                    <CardTitle className="text-base">
                      {expert?.name || DEFAULT_EXPERT.name}
                    </CardTitle>
                    {!hidePoints && (
                      <Badge
                        variant="outline"
                        className="ml-2 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300 text-xs flex items-center px-2 py-1"
                      >
                        <Coins className="mr-1 h-3 w-3" />
                        {requiredPoints} {t("points_per_chat", { ns: "common" })}
                      </Badge>
                    )}
                  </div>
                  <CardDescription className="text-xs">
                    {expert?.title || DEFAULT_EXPERT.title}
                  </CardDescription>
                </div>
              </div>
              {!hideCloseButton && onClose && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={onClose}
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </CardHeader>
      )}

      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        {!hasEnoughPoints && !hideStartChatButton ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-4">
            <MessageCircle className="h-12 w-12 mb-4 text-primary" />
            <h3 className="font-medium text-lg mb-2">
              {t("chat_with_expert", { expert: expert?.name })}
            </h3>
            <p className="text-muted-foreground mb-4 max-w-md">
              {expert?.greeting ||
                t("expert_greeting", { expert: expert?.name })}
            </p>
            <Button
              onClick={() => {}}
              className="mb-4"
              disabled={true}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <MessageSquare className="h-4 w-4 mr-2" />
              )}
              {`Cần ít nhất ${requiredPoints} điểm để chat`}
            </Button>
          </div>
        ) : isFetchingCourse && contentType !== "diagram" ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-4">
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <p>{t("loading", { ns: "common" })}</p>
            </div>
          </div>
        ) : (contentType === "course" && !window.courseData?.nodes && !courseNodes.length && !isFetchingCourse) ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-4">
            <AlertTriangle className="h-12 w-12 mb-4 text-red-500" />
            <h3 className="font-medium text-lg mb-2">
              Không thể tải dữ liệu khóa học
            </h3>
            <p className="text-muted-foreground mb-4 max-w-md">
              Vui lòng thử lại sau
            </p>
          </div>
        ) : (
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-hidden">
              <ChatWithAI
                contextData={prepareContextData()}
                isWidget={false}
                hideHeader={true}
                onMessageSent={handleMessageSent}
                fullHeight={true}
                skipSaveHistory={skipSaveHistory}
              />
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export default ContentChatWidget;